import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';
import { projects } from '../data/projectsData';

const Work = () => {
  const { t } = useLanguage();
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Generate dynamic meta description based on selected project
  const metaDescription = selectedProject 
    ? selectedProject.metaDescription 
    : "Interior design portfolio: Residential and hospitality projects by Element studio. Award-winning design work in Sofia, Milano and across Europe.";

  const openProject = useCallback((project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0); // Reset to first image
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  }, [setSelectedProject, setCurrentImageIndex]);

  const closeProject = useCallback(() => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'unset'; // Re-enable scroll
  }, [setSelectedProject, setCurrentImageIndex]);

  const nextImage = useCallback(() => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  }, [selectedProject, setCurrentImageIndex]);

  const previousImage = useCallback(() => {
    if (selectedProject) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  }, [selectedProject, setCurrentImageIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedProject) return;
      
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') previousImage();
      if (e.key === 'Escape') closeProject();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedProject, nextImage, previousImage, closeProject]);

  return (
    <>
      <Helmet>
        <title>
          {selectedProject ? `${selectedProject.title} — Interior Design Work | Element by Elena Mustakova` : 'Interior Design Work | Element by Elena Mustakova'}
        </title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href="https://mustakova.com/work" />
      </Helmet>
      
      <section className="min-h-screen py-24 md:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Heading - Larger */}
        <div className="mb-20">
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-light text-[#2C2C2C] lowercase mb-8">
            {t('work.heading')}
          </h1>
          <p className="text-2xl md:text-3xl font-light text-[#2C2C2C] mb-4">
            {t('work.intro')}
          </p>
          <p className="text-xl font-light text-[#5A5A5A]">
            {t('work.subline')}
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <button
              key={project.id}
              onClick={() => openProject(project)}
              className="group block text-left"
            >
              <div className="relative overflow-hidden bg-[#F6F4F1] mb-4 aspect-[3/4]">
                <img
                  src={project.coverImage}
                  alt={project.coverImageAlt || project.title}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                  style={project.coverImagePosition ? { objectPosition: project.coverImagePosition } : {}}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                
                {project.status === 'coming_soon' && (
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1">
                    <span className="text-xs tracking-widest uppercase text-[#2C2C2C] font-light">
                      Coming Soon
                    </span>
                  </div>
                )}
              </div>
              
              <div>
                <p className="text-sm tracking-widest uppercase text-[#6B6B6B] mb-2 font-light">
                  {project.category}
                </p>
                <h3 className="text-2xl font-light text-[#2C2C2C] group-hover:text-[#2C1810] transition-colors duration-300">
                  {project.title}
                </h3>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          {/* Header with Beige Background */}
          <div className="sticky top-0 z-10 bg-[#F6F4F1] border-b border-[#E5E5E5] px-6 py-8">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div>
                <p className="text-sm tracking-widest uppercase text-[#6B6B6B] mb-2 font-light">
                  {selectedProject.category}
                </p>
                <h2 className="text-3xl md:text-4xl font-light text-[#2C2C2C]">
                  {selectedProject.title}
                </h2>
              </div>
              <button
                onClick={closeProject}
                className="text-[#2C2C2C] hover:text-[#2C1810] transition-colors p-2"
                aria-label="Close project"
              >
                <X className="w-8 h-8" />
              </button>
            </div>
          </div>

          {/* Project Description - Smaller Text */}
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="grid md:grid-cols-2 md:gap-12 text-lg md:text-xl font-light text-[#2C2C2C] leading-relaxed items-start">
              <div className="space-y-6">
                {selectedProject.description.split('\n\n').map((paragraph, index) => {
                  const paragraphs = selectedProject.description.split('\n\n');
                  const midPoint = Math.ceil(paragraphs.length / 2);
                  
                  if (index < midPoint) {
                    return (
                      <p key={`${selectedProject.id}-left-${index}`}>
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
              <div className="space-y-6">
                {selectedProject.description.split('\n\n').map((paragraph, index) => {
                  const paragraphs = selectedProject.description.split('\n\n');
                  const midPoint = Math.ceil(paragraphs.length / 2);
                  
                  if (index >= midPoint) {
                    return (
                      <p key={`${selectedProject.id}-right-${index}`}>
                        {paragraph}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
            
            {selectedProject.location && (
              <p className="text-lg font-light text-[#5A5A5A] mt-8">
                {selectedProject.location}
              </p>
            )}
            
            {selectedProject.photographer && (
              <p className="text-sm font-light text-[#6B6B6B] mt-2 italic">
                Photography: {selectedProject.photographer}
              </p>
            )}
            
            {selectedProject.videoUrl && (
              <a
                href={selectedProject.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6 text-[#2C1810] border-b border-[#2C1810] hover:opacity-70 transition-opacity font-light"
              >
                Watch Video →
              </a>
            )}
          </div>

          {/* Horizontal Image Carousel */}
          <div className="relative max-w-7xl mx-auto px-6 pb-20">
            {/* Current Image/Video */}
            <div className="w-full">
              {selectedProject.images[currentImageIndex]?.isVideo ? (
                <video
                  src={selectedProject.images[currentImageIndex].url}
                  poster={selectedProject.images[currentImageIndex].poster}
                  alt={selectedProject.images[currentImageIndex].alt}
                  className="w-full h-auto"
                  controls
                  loop
                  muted
                  playsInline
                  preload="metadata"
                />
              ) : (
                <img
                  src={selectedProject.images[currentImageIndex]?.url}
                  alt={selectedProject.images[currentImageIndex]?.alt}
                  className={`w-full ${selectedProject.images[currentImageIndex]?.url.includes('Sketch') ? 'max-h-[600px] object-contain bg-white' : 'h-auto'}`}
                  loading="lazy"
                />
              )}
            </div>

            {/* Navigation Controls */}
            {selectedProject.images.length > 1 && (
              <>
                {/* Previous Button */}
                <button
                  onClick={previousImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300"
                  aria-label="Previous image"
                >
                  <svg className="w-6 h-6 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {/* Next Button */}
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300"
                  aria-label="Next image"
                >
                  <svg className="w-6 h-6 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* Image Counter */}
                <div className="text-center mt-6">
                  <span className="text-sm text-[#5A5A5A] font-light">
                    {currentImageIndex + 1} / {selectedProject.images.length}
                  </span>
                </div>

                {/* Thumbnail Navigation */}
                <div className="flex justify-center gap-2 mt-4 flex-wrap max-w-4xl mx-auto">
                  {selectedProject.images.map((media, index) => (
                    <button
                      key={`thumb-${index}`}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-all duration-300 ${
                        index === currentImageIndex
                          ? 'border-[#2C1810] opacity-100'
                          : 'border-gray-300 opacity-60 hover:opacity-100'
                      }`}
                    >
                      {media.isVideo ? (
                        <img
                          src={media.poster}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={media.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
    </>
  );
};

export default Work;
